import { Box } from '@mui/material';
import { ChartCard } from '../components/Cards/ChartCard';
import { DonutChart } from '../components/Charts';
import { useProgramCharts } from './hooks/useProgramCharts';

const formatTierLabel = (tier: string): string =>
  tier
    .replace(/\bDyad\b/g, 'DYAD')
    .replace(/\s*Tier\s*$/i, '')
    .trim();

export const ProgramCharts = () => {
  const {
    hasViewPermission,
    ccmPatientsByMinutes,
    patientsInCareManagement,
    patientsInServiceLines,
    memoryConnectStatusMetrics,
    memoryConnectTierMetrics,
    guideStatusesMetrics,
    guideTierMetrics,
    isLoadingCareManagement,
    isLoadingServiceLines,
    isLoadingCcm,
    isLoadingGuideStatus,
    isLoadingGuideTier,
    isLoadingMcStatus,
    isLoadingMcTier,
  } = useProgramCharts();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `1fr`,
        gap: 2,
      }}
    >
      <ChartCard
        title='Patients in Care Management'
        report_id={patientsInCareManagement?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingCareManagement}
      >
        <DonutChart
          entries={
            patientsInCareManagement?.patient_counts_by_program.map(o => ({
              value: o.patient_count,
              label: o.program_name,
            })) ?? []
          }
        />
      </ChartCard>
      <ChartCard
        title='Patients in Service Lines'
        report_id={patientsInServiceLines?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingServiceLines}
      >
        <DonutChart
          entries={
            patientsInServiceLines?.patient_counts_by_program.map(o => ({
              value: o.patient_count,
              label: o.program_name,
            })) ?? []
          }
        />
      </ChartCard>
      <ChartCard
        title='CCM Patients Minutes'
        report_id={ccmPatientsByMinutes?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingCcm}
      >
        <DonutChart
          preserveOrder
          otherThreshold={6}
          entries={[
            {
              value: ccmPatientsByMinutes?.patients_with_0_minutes ?? 0,
              label: 'Patients with 0 minutes',
            },
            {
              value:
                ccmPatientsByMinutes?.patients_with_less_than_20_minutes ?? 0,
              label: 'Patients with 1-19 minutes',
            },
            {
              value:
                ccmPatientsByMinutes?.patients_with_less_than_60_minutes ?? 0,
              label: 'Patients with 20-59 minutes',
            },
            {
              value:
                ccmPatientsByMinutes?.patients_with_less_than_120_minutes ?? 0,
              label: 'Patients with 60-119 minutes',
            },
            {
              value:
                ccmPatientsByMinutes?.patients_with_more_than_120_minutes ?? 0,
              label: 'Patients with 120+ minutes',
            },
          ]}
        />
      </ChartCard>
      <ChartCard
        title='GUIDE program status'
        report_id={guideStatusesMetrics?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingGuideStatus}
      >
        <DonutChart
          entries={
            guideStatusesMetrics?.patient_counts_by_guide_status.map(o => ({
              value: o.patient_count,
              label: o.status,
            })) ?? []
          }
        />
      </ChartCard>
      <ChartCard
        title='GUIDE Tier'
        report_id={guideTierMetrics?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingGuideTier}
      >
        <DonutChart
          entries={
            guideTierMetrics?.patient_counts_by_guide_tier.map(o => ({
              value: o.patient_count,
              label: formatTierLabel(o.tier),
            })) ?? []
          }
        />
      </ChartCard>

      <ChartCard
        title='Optum MA status'
        report_id={memoryConnectStatusMetrics?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingMcStatus}
      >
        <DonutChart
          entries={
            memoryConnectStatusMetrics?.patient_counts_by_mc_status.map(o => ({
              value: o.patient_count,
              label: o.status,
            })) ?? []
          }
        />
      </ChartCard>

      <ChartCard
        title='Optum MA tier'
        report_id={memoryConnectTierMetrics?.linked_report_id}
        hideButton={!hasViewPermission}
        isLoading={isLoadingMcTier}
      >
        <DonutChart
          entries={
            memoryConnectTierMetrics?.patient_counts_by_mc_tier.map(o => ({
              value: o.patient_count,
              label: formatTierLabel(o.tier),
            })) ?? []
          }
        />
      </ChartCard>
    </Box>
  );
};
